/**
 * SVG paths for the Material Design Icons the app shows: every `mdi-` name in
 * the code, plus the ones Vuetify's own components ask for through the
 * default aliases this app does not override (`$expand`, `$prev`, `$delete`…).
 *
 * Only these are bundled. A name missing here still renders: the full set is
 * then loaded once on demand (see `src/helpers/mdiSvgIcon.ts`), so add new
 * icons here to keep them out of that slower path.
 */
import {
  mdiAlert,
  mdiAlertCircle,
  mdiAlphaABoxOutline,
  mdiAppleKeyboardCommand,
  mdiAppleKeyboardControl,
  mdiAppleKeyboardOption,
  mdiAppleKeyboardShift,
  mdiArrowDown,
  mdiArrowExpandAll,
  mdiArrowLeft,
  mdiArrowLeftRight,
  mdiArrowRight,
  mdiArrowUp,
  mdiBackspace,
  mdiCached,
  mdiCalendar,
  mdiCancel,
  mdiCheck,
  mdiCheckCircle,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUp,
  mdiCircle,
  mdiClockOutline,
  mdiClose,
  mdiCloseCircle,
  mdiCloudOutline,
  mdiCloudUpload,
  mdiCloudUploadOutline,
  mdiDotsVertical,
  mdiDraw,
  mdiEyedropper,
  mdiFacebook,
  mdiFormatLineStyle,
  mdiFormatRotate90,
  mdiFormatText,
  mdiFormatTitle,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiGoogleSpreadsheet,
  mdiHistory,
  mdiKeyboardReturn,
  mdiKeyboardSpace,
  mdiLoading,
  mdiLogin,
  mdiLogout,
  mdiMagnify,
  mdiMenu,
  mdiMenuDown,
  mdiMenuRight,
  mdiMinus,
  mdiOpenInNew,
  mdiPageFirst,
  mdiPageLast,
  mdiPalette,
  mdiPaperclip,
  mdiPause,
  mdiPauseCircle,
  mdiPencil,
  mdiPlay,
  mdiPlus,
  mdiRefresh,
  mdiRestore,
  mdiRotate360,
  mdiRotate3d,
  mdiStar,
  mdiStarBoxMultipleOutline,
  mdiStarHalfFull,
  mdiStarOutline,
  mdiStop,
  mdiSync,
  mdiTableLargePlus,
  mdiUndo,
  mdiUnfoldMoreHorizontal,
  mdiVolumeHigh,
  mdiVolumeLow,
  mdiVolumeMedium,
  mdiVolumeVariantOff,
  mdiWeatherNight,
  mdiWeatherSunny
} from '@mdi/js';

export const MDI_PATHS: Readonly<Record<string, string>> = {
  'mdi-alert': mdiAlert,
  'mdi-alert-circle': mdiAlertCircle,
  'mdi-alpha-a-box-outline': mdiAlphaABoxOutline,
  'mdi-apple-keyboard-command': mdiAppleKeyboardCommand,
  'mdi-apple-keyboard-control': mdiAppleKeyboardControl,
  'mdi-apple-keyboard-option': mdiAppleKeyboardOption,
  'mdi-apple-keyboard-shift': mdiAppleKeyboardShift,
  'mdi-arrow-down': mdiArrowDown,
  'mdi-arrow-expand-all': mdiArrowExpandAll,
  'mdi-arrow-left': mdiArrowLeft,
  'mdi-arrow-left-right': mdiArrowLeftRight,
  'mdi-arrow-right': mdiArrowRight,
  'mdi-arrow-up': mdiArrowUp,
  'mdi-backspace': mdiBackspace,
  'mdi-cached': mdiCached,
  'mdi-calendar': mdiCalendar,
  'mdi-cancel': mdiCancel,
  'mdi-check': mdiCheck,
  'mdi-check-circle': mdiCheckCircle,
  'mdi-chevron-down': mdiChevronDown,
  'mdi-chevron-left': mdiChevronLeft,
  'mdi-chevron-right': mdiChevronRight,
  'mdi-chevron-up': mdiChevronUp,
  'mdi-circle': mdiCircle,
  'mdi-clock-outline': mdiClockOutline,
  'mdi-close': mdiClose,
  'mdi-close-circle': mdiCloseCircle,
  'mdi-cloud-outline': mdiCloudOutline,
  'mdi-cloud-upload': mdiCloudUpload,
  'mdi-cloud-upload-outline': mdiCloudUploadOutline,
  'mdi-dots-vertical': mdiDotsVertical,
  'mdi-draw': mdiDraw,
  'mdi-eyedropper': mdiEyedropper,
  'mdi-facebook': mdiFacebook,
  'mdi-format-line-style': mdiFormatLineStyle,
  'mdi-format-rotate-90': mdiFormatRotate90,
  'mdi-format-text': mdiFormatText,
  'mdi-format-title': mdiFormatTitle,
  'mdi-fullscreen': mdiFullscreen,
  'mdi-fullscreen-exit': mdiFullscreenExit,
  'mdi-google-spreadsheet': mdiGoogleSpreadsheet,
  'mdi-history': mdiHistory,
  'mdi-keyboard-return': mdiKeyboardReturn,
  'mdi-keyboard-space': mdiKeyboardSpace,
  'mdi-loading': mdiLoading,
  'mdi-login': mdiLogin,
  'mdi-logout': mdiLogout,
  'mdi-magnify': mdiMagnify,
  'mdi-menu': mdiMenu,
  'mdi-menu-down': mdiMenuDown,
  'mdi-menu-right': mdiMenuRight,
  'mdi-minus': mdiMinus,
  'mdi-open-in-new': mdiOpenInNew,
  'mdi-page-first': mdiPageFirst,
  'mdi-page-last': mdiPageLast,
  'mdi-palette': mdiPalette,
  'mdi-paperclip': mdiPaperclip,
  'mdi-pause': mdiPause,
  'mdi-pause-circle': mdiPauseCircle,
  'mdi-pencil': mdiPencil,
  'mdi-play': mdiPlay,
  'mdi-plus': mdiPlus,
  'mdi-refresh': mdiRefresh,
  'mdi-restore': mdiRestore,
  'mdi-rotate-360': mdiRotate360,
  'mdi-rotate-3d': mdiRotate3d,
  'mdi-star': mdiStar,
  'mdi-star-box-multiple-outline': mdiStarBoxMultipleOutline,
  'mdi-star-half-full': mdiStarHalfFull,
  'mdi-star-outline': mdiStarOutline,
  'mdi-stop': mdiStop,
  'mdi-sync': mdiSync,
  'mdi-table-large-plus': mdiTableLargePlus,
  'mdi-undo': mdiUndo,
  'mdi-unfold-more-horizontal': mdiUnfoldMoreHorizontal,
  'mdi-volume-high': mdiVolumeHigh,
  'mdi-volume-low': mdiVolumeLow,
  'mdi-volume-medium': mdiVolumeMedium,
  'mdi-volume-variant-off': mdiVolumeVariantOff,
  'mdi-weather-night': mdiWeatherNight,
  'mdi-weather-sunny': mdiWeatherSunny
};
